/**
 * Fetches data from the specified URL and returns the parsed JSON response.
 *
 * @async
 * @param {string} url - The URL to fetch data from.
 * @returns {Promise<Object>} The parsed JSON data from the response.
 */
export const getQuery = async (url) => {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Error fetching data from ${url}: ${response.status} ${response.statusText}`);
    } else {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Safely parses a response body as JSON, returning null for empty or non-JSON bodies.
 *
 * @param {string} body - The raw response text.
 * @returns {Object|null} Parsed JSON, or null if the body is empty or not valid JSON.
 */
const safeJsonParse = (body) => {
  if (!body) return null;
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
};

/**
 * Posts data to the specified URL and returns the parsed JSON response.
 *
 * @async
 * @param {string} url - The URL to post data to.
 * @param {Object} data - The data to post in the request body.
 * @param {Object} [options={}] - Additional fetch options (headers, etc.).
 * @returns {Promise<Object>} The parsed JSON data from the response.
 * @throws {Error} If the request fails or response is not ok.
 */
export const postQuery = async (url, data, options = {}) => {
  const { headers: customHeaders, ...restOptions } = options;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...customHeaders,
    },
    body: JSON.stringify(data),
    ...restOptions,
  });

  if (!response.ok) {
    const errorBody = await response.text();
    const parsed = safeJsonParse(errorBody);
    const errorMessage = parsed?.error || `Error posting data to ${url}: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  const text = await response.text();
  return safeJsonParse(text);
};
